import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { ApiError } from '../../../lib/api/errors.js';
import { useApiQuery } from '../../../lib/api/hooks.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { z } from 'zod';
import { RoomTypeAmenitiesFields } from '../forms/room-type-amenities-fields.js';
import { useCreateRoomType, useUpdateRoomType } from '../api/mutations.js';
import { useRoomType, useRoomTypeAmenities } from '../api/queries.js';
import {
  type RoomTypeAmenityOption,
  type RoomTypeDetail,
  type RoomTypeFormInput,
  roomTypeFormInput,
} from '../api/schemas.js';
import { RoomTypeFields } from '../forms/room-type-fields.js';

/** 409. Named because the linter forbids a bare numeric literal here. */
const HTTP_CONFLICT = 409;

function toAmenityOptions(
  rows: readonly { readonly value: string; readonly label: string }[],
): readonly RoomTypeAmenityOption[] {
  return rows.map((amenity) => ({
    amenityId: amenity.value,
    name: amenity.label,
    isFree: true,
    isSelected: false,
  }));
}

function defaultsFrom(
  roomType: RoomTypeDetail,
  amenityOptions: readonly RoomTypeAmenityOption[],
): RoomTypeFormInput {
  const selectedAmenities: Record<string, boolean> = {};
  const amenityFree: Record<string, boolean> = {};

  for (const amenity of amenityOptions) {
    selectedAmenities[amenity.amenityId] = amenity.isSelected;
    amenityFree[amenity.amenityId] = amenity.isFree;
  }

  return {
    propertyId: roomType.propertyId,
    code: roomType.code,
    name: roomType.name,
    displayName: roomType.displayName ?? '',
    description: roomType.description ?? '',
    maxOccupancy: String(roomType.maxOccupancy),
    sizeInSqm: roomType.sizeInSqm ?? '',
    bedType: roomType.bedType ?? '',
    totalNoOfRooms: String(roomType.totalNoOfRooms),
    pmsRoomTypeId: roomType.pmsRoomTypeId ?? '',
    status: roomType.status,
    selectedAmenities,
    amenityFree,
  };
}

export function RoomTypeFormPage(): React.JSX.Element {
  const roomTypeId = useParams().roomTypeId;
  const isEdit = roomTypeId !== undefined;

  return (
    <PermissionGuard
      permission={isEdit ? PERMISSIONS.ROOM_TYPE_UPDATE : PERMISSIONS.ROOM_TYPE_CREATE}
      fallback={<NotPermitted />}
    >
      {isEdit ? <EditRoomType roomTypeId={roomTypeId} /> : <CreateRoomType />}
    </PermissionGuard>
  );
}

function CreateRoomType(): React.JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const create = useCreateRoomType();
  const propertyId = params.get('propertyId') ?? '';

  const allAmenities = useApiQuery({
    queryKey: ['amenities', 'reference', 'options-by-type', 'ROOM'],
    path: '/v1/amenities/options-by-type',
    schema: z.array(z.object({ value: z.string(), label: z.string() })),
    query: { type: 'ROOM' },
  });

  const defaults: RoomTypeFormInput = {
    propertyId,
    code: '',
    name: '',
    displayName: '',
    description: '',
    maxOccupancy: '2',
    sizeInSqm: '',
    bedType: '',
    totalNoOfRooms: '0',
    pmsRoomTypeId: '',
    status: 'ACTIVE',
    selectedAmenities: {},
    amenityFree: {},
  };

  const form = useAppForm<RoomTypeFormInput>({
    schema: roomTypeFormInput,
    defaultValues: defaults,
    successMessage: 'Room type created',
    onSubmit: async (values) => {
      const created = await create.mutateAsync(values);
      void navigate(`/room-types/${created.id}`);
    },
  });

  if (allAmenities.isPending) {
    return <LoadingState label="Loading room amenities" />;
  }

  if (allAmenities.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(allAmenities.error) ?? ''}
        onRetry={() => {
          void allAmenities.refetch();
        }}
      />
    );
  }

  const amenityList = toAmenityOptions(allAmenities.data);

  return (
    <FormPage
      title="New room type"
      description="A room type is what a guest books; it carries the counts that availability sells from."
      backTo={
        propertyId === ''
          ? '/room-types'
          : `/room-types?propertyId=${encodeURIComponent(propertyId)}`
      }
      backLabel="Room types"
      submitLabel="Create room type"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <RoomTypeFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        mode="create"
      />
      <RoomTypeAmenitiesFields form={form} amenities={amenityList} />
    </FormPage>
  );
}

function EditRoomType({ roomTypeId }: { readonly roomTypeId: string }): React.JSX.Element {
  const navigate = useNavigate();
  const update = useUpdateRoomType();
  const roomTypeQuery = useRoomType(roomTypeId);
  const amenitiesQuery = useRoomTypeAmenities(roomTypeId);

  if (roomTypeQuery.isPending || amenitiesQuery.isPending) {
    return <LoadingState label="Loading room type" />;
  }

  if (roomTypeQuery.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(roomTypeQuery.error) ?? ''}
        onRetry={() => {
          void roomTypeQuery.refetch();
        }}
      />
    );
  }

  if (amenitiesQuery.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(amenitiesQuery.error) ?? ''}
        onRetry={() => {
          void amenitiesQuery.refetch();
        }}
      />
    );
  }

  return (
    <EditRoomTypeForm
      roomTypeId={roomTypeId}
      roomType={roomTypeQuery.data}
      amenityOptions={amenitiesQuery.data.amenities}
      onSaved={() => {
        void navigate(`/room-types/${roomTypeId}`);
      }}
      save={async (values) => {
        try {
          await update.mutateAsync({ roomTypeId, version: roomTypeQuery.data.version, values });
        } catch (error) {
          if (error instanceof ApiError && error.status === HTTP_CONFLICT) {
            await roomTypeQuery.refetch();
          }
          throw error;
        }
      }}
    />
  );
}

function EditRoomTypeForm({
  roomTypeId,
  roomType,
  amenityOptions,
  save,
  onSaved,
}: {
  readonly roomTypeId: string;
  readonly roomType: RoomTypeDetail;
  readonly amenityOptions: readonly RoomTypeAmenityOption[];
  readonly save: (values: RoomTypeFormInput) => Promise<void>;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const form = useAppForm<RoomTypeFormInput>({
    schema: roomTypeFormInput,
    defaultValues: defaultsFrom(roomType, amenityOptions),
    successMessage: 'Room type updated',
    onSubmit: async (values) => {
      await save(values);
      onSaved();
    },
  });

  return (
    <FormPage
      title={roomType.name}
      description={`Code ${roomType.code}. Editing the room type details.`}
      backTo={`/room-types/${roomTypeId}`}
      backLabel="Back to room type"
      submitLabel="Save changes"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <RoomTypeFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        mode="edit"
      />
      <RoomTypeAmenitiesFields form={form} amenities={amenityOptions} />
    </FormPage>
  );
}
